import { AlertTriangle, Check, RotateCcw } from 'lucide-react';
import { PortalFollowUpStatus } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { PendingButton } from '@/components/ui/pending-button';
import {
  usePortalFollowUps,
  useCompletePortalFollowUp,
  useReopenPortalFollowUp,
} from '@/hooks/queries/usePortalFollowUps';

interface PortalFollowUpsChecklistProps {
  reportId: string;
}

/**
 * GitHub #34 — the durable per-report "Portal follow-ups" checklist. Portal-handoff
 * obligations the electronic filing itself can't satisfy (an AL In-Kind "Other" row keyed by
 * hand in the FCPA portal, a refund's record-then-amend step, a Major Contribution Report,
 * per-loan guarantors, the portal-only AL amendment) are snapshotted at file time so a
 * treasurer returning to a Filed report still has a record of what they owe the portal.
 *
 * Open items are pinned first (amber, actionable "Mark done in portal"); Done items render
 * muted/checked with a Reopen affordance. Empty (no rows) → renders nothing. Completing a
 * follow-up gates nothing (surface-don't-block).
 */
export function PortalFollowUpsChecklist({ reportId }: PortalFollowUpsChecklistProps) {
  const { data: followUps } = usePortalFollowUps(reportId);
  const { completeFollowUp, isCompleting } = useCompletePortalFollowUp();
  const { reopenFollowUp, isReopening } = useReopenPortalFollowUp();

  if (!followUps || followUps.length === 0) return null;

  const open = followUps.filter((f) => f.status === PortalFollowUpStatus.Open);
  const done = followUps.filter((f) => f.status === PortalFollowUpStatus.Done);

  return (
    <div
      className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4"
      data-testid="portal-follow-ups-checklist"
    >
      <div className="flex items-center gap-2 text-sm font-medium text-amber-900">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        Portal follow-ups
        {open.length > 0 && (
          <span
            className="ml-1 rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-900"
            data-testid="portal-follow-ups-open-count"
          >
            {open.length} open
          </span>
        )}
      </div>
      <p className="text-xs text-amber-800">
        These are manual actions you still owe the state portal — the filing itself can&apos;t
        do them for you.
      </p>

      <ul className="space-y-2">
        {open.map((f) => (
          <li
            key={f.id}
            className="flex items-start justify-between gap-3 rounded-md border border-amber-300 bg-white p-3"
            data-testid="portal-follow-up-open"
          >
            <p className="text-sm text-gray-800">{f.description}</p>
            <PendingButton
              variant="outline"
              size="sm"
              className="shrink-0"
              pending={isCompleting}
              icon={<Check className="h-4 w-4" />}
              onClick={() => completeFollowUp({ reportId, followUpId: f.id })}
              data-testid="portal-follow-up-complete"
            >
              Mark done in portal
            </PendingButton>
          </li>
        ))}

        {done.map((f) => (
          <li
            key={f.id}
            className="flex items-start justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 p-3"
            data-testid="portal-follow-up-done"
          >
            <div className="flex items-start gap-2 text-sm text-gray-500">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
              <p className="line-through">{f.description}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 text-gray-500"
              disabled={isReopening}
              onClick={() => reopenFollowUp({ reportId, followUpId: f.id })}
              data-testid="portal-follow-up-reopen"
            >
              <RotateCcw className="mr-1 h-4 w-4" />
              Reopen
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
